import get from 'lodash-es/get'
import useSwr, { SWRConfiguration, SWRResponse } from 'swr'

import {
  AnalyticsQueryDatapointEnum,
  AnalyticsQueryQueryEnum,
  AnalyticsResponse,
  ErrorResponse,
  NetworkType
} from '@infura/packages.api'
import { Product } from '@infura/packages.api'
import { isAxiosError } from '@infura/packages.common/errors'

import { api } from 'lib/api/api'
import statsUtils, { AnalyticsQueryKeys, makeStat1_5Query, makeStatQuery } from 'lib/statsUtils'

const defaultStatsFetcherConfig: SWRConfiguration = {
  errorRetryCount: 1,
  errorRetryInterval: 60000,
  focusThrottleInterval: 30000,
  loadingTimeout: 30000,
  revalidateOnFocus: false
}

export interface UseStatsQueryParams {
  query: AnalyticsQueryQueryEnum
  datapoint: AnalyticsQueryDatapointEnum
  time: string
  intervals?: string
  product: Product
  projectIds: Array<string>
  config?: SWRConfiguration
  method?: string
  network?: string
  shouldFetch?: () => boolean // Provide a function that tells the fetcher to refetch or not.
}

export async function constantStatsFetcher([query, product, projectIds, network]: [
  AnalyticsQueryKeys,
  Product,
  Array<string>,
  NetworkType
]) {
  try {
    return (
      await api.getAnalytics({
        queries: get(statsUtils, [query, 'queries'], []).map(q => ({
          ...q,
          ...(network ? { network } : {}),
          for: { public_key: projectIds },
          product
        }))
      })
    ).data
  } catch (e: any) {
    if (isAxiosError(e)) {
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      throw e.response.data
    }
    throw e
  }
}

export default function useConstantStatsQuery(
  query: AnalyticsQueryKeys,
  product: Product,
  projectIds: Array<string>,
  network?: NetworkType,
  config?: SWRConfiguration & {
    disabled?: boolean
  }
): SWRResponse<AnalyticsResponse, ErrorResponse> {
  const swr = useSwr<AnalyticsResponse, ErrorResponse>(
    !config?.disabled ? [query, product, projectIds, network] : null,
    {
      ...config,
      fetcher: constantStatsFetcher,
      refreshWhenHidden: false, // Don't refresh when the page is not visible
      refreshWhenOffline: false
    }
  )
  return swr
}

export async function dynamicStatsFetcher([query, datapoint, time, userTimestamp, product, ...projectIds]: [
  AnalyticsQueryQueryEnum,
  AnalyticsQueryDatapointEnum,
  string,
  string,
  Product,
  ...Array<string>
]) {
  try {
    return (
      await api.getAnalytics({
        queries: [makeStatQuery(query, datapoint, time, userTimestamp, product, projectIds)]
      })
    ).data
  } catch (e: any) {
    if (isAxiosError(e)) {
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      throw e.response.data
    }
    throw e
  }
}

export function useDynamicStatsQuery(
  query: AnalyticsQueryQueryEnum,
  datapoint: AnalyticsQueryDatapointEnum,
  time: string,
  userTimestamp: string,
  product: Product,
  projectIds: Array<string>,
  config?: SWRConfiguration
): SWRResponse<AnalyticsResponse, ErrorResponse> {
  const swr = useSwr<AnalyticsResponse, ErrorResponse>(
    [query, datapoint, time, userTimestamp, product, ...projectIds],
    dynamicStatsFetcher,
    config
  )
  return swr
}
export async function dynamicStats1_5_Fetcher([
  query,
  datapoint,
  time,
  intervals,
  product,
  method,
  network,
  ...projectIds
]: [AnalyticsQueryQueryEnum, AnalyticsQueryDatapointEnum, string, string, Product, string, string, ...Array<string>]) {
  try {
    switch (query) {
      case AnalyticsQueryQueryEnum.TIMESERIES:
        return (
          await api.getRequestVolumeStats(
            makeStat1_5Query({
              datapoint,
              intervals,
              product,
              projectIds,
              query,
              time,
              userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
            })
          )
        ).data
      case AnalyticsQueryQueryEnum.ACTIVITY:
        return (
          await api.getRequestActivityStats(
            makeStat1_5Query({
              datapoint,
              intervals,
              product,
              projectIds,
              query,
              time,
              userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
            })
          )
        ).data
      case AnalyticsQueryQueryEnum.METHOD_DOMINANCE:
        return (
          await api.getMethodDominanceStats(
            makeStat1_5Query({
              datapoint,
              intervals,
              network,
              product,
              projectIds,
              query,
              time,
              userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
            })
          )
        ).data
      case AnalyticsQueryQueryEnum.NETWORK_DOMINANCE:
        return (
          await api.getNetworkDominanceStats(
            makeStat1_5Query({
              datapoint,
              intervals,
              method,
              product,
              projectIds,
              query,
              time,
              userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
            })
          )
        ).data
      case AnalyticsQueryQueryEnum.ETH_CALL_ACTIVITY:
        return (
          await api.getEthCallActivityStats(
            makeStat1_5Query({
              datapoint,
              intervals,
              product,
              projectIds,
              query,
              time,
              userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
            })
          )
        ).data
    }
  } catch (e: any) {
    if (isAxiosError(e)) {
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      throw e.response.data
    }
    throw e
  }
}

export function useDynamicStats1_5_Query({
  config,
  datapoint,
  intervals,
  // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
  method = null,
  // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
  network = null,
  product,
  projectIds,
  query,
  shouldFetch,
  time
}: UseStatsQueryParams & { refreshInterval?: number }): SWRResponse<AnalyticsResponse, ErrorResponse> {
  // Account for all products string being passed in.
  if (projectIds[0] === 'all-projects') projectIds = []

  const swrConfig = {
    ...defaultStatsFetcherConfig,
    ...config,
    fetcher: dynamicStats1_5_Fetcher,
    refreshWhenHidden: false, // don't refresh when the page is not visible
    refreshWhenOffline: false
  }

  let swrKey = [query, datapoint, time, intervals, product, method, network, ...projectIds]

  if (shouldFetch) {
    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    swrKey = shouldFetch() ? swrKey : null
  }

  // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
  return useSwr<AnalyticsResponse, ErrorResponse>(swrKey, swrConfig)
}
